import React, { useContext, useMemo } from 'react';
import Link from 'components/CustomLink';
import qs from 'qs';
import Image from 'next/legacy/image';
import { Box, Divider, Skeleton, Typography } from '@mui/material';

import arrowLeft from 'public/images/arrow-left.svg';

import { BuildingPageContext } from '../../pages/[urlParam]/[urlBuildParam]';
import { getSearchLink } from '../Search/utils/listingsFiltersUtils';
import { BuildingPageContextInterface } from '../../apolloClient/types/Building';

import Button from '../Button';
import CondoCard from '../Search/CondosSearchResult/CondoCard';
import MarkdownParser from '../MarkdownParser';
import classnames from 'classnames';
import LineBetweenSections from 'components/LineBetweenSections';
import SectionHeader from '../common/SectionHeader';
import ResponsiveCarousel from '../common/ResponsiveCarousel';

const NearbyCondos: React.FC = () => {
  const { neighborhood, nearbyCondos, calculatedParams } = useContext(
    BuildingPageContext
  ) as BuildingPageContextInterface;

  const searchPageLink = useMemo(() => {
    if (calculatedParams.unused) {
      return getSearchLink({
        neighborhoods: [neighborhood.slug],
        preConstruction: true,
        newConstruction: true,
        foreclosures: true,
        shortSales: true,
        existingCondos: true,
        comingSoon: true,
        forRent: true,
      });
    }
    if (calculatedParams) {
      return getSearchLink({
        neighborhoods: [neighborhood.slug],
        anyPrice: {
          max: String(calculatedParams.maxPrice),
          min: String(calculatedParams.minPrice),
        },
        sqft: {
          max: String(calculatedParams.maxArea),
          min: String(calculatedParams.minArea),
        },
        preConstruction: true,
        newConstruction: true,
        foreclosures: true,
        shortSales: true,
        existingCondos: true,
        comingSoon: true,
        forRent: true,
      });
    }
    return qs.stringify({ neighborhood: neighborhood?.slug });
  }, [calculatedParams, neighborhood]);

  const Preloader = () => (
    <>
      {new Array(1).fill(null).map((_, i) => (
        <Box pt={3} key={i}>
          {new Array(10).fill(null).map((__, j) => (
            <Typography key={j} variant="h3">
              <Skeleton />
            </Typography>
          ))}
          <Divider sx={{ marginTop: 3 }} />
        </Box>
      ))}
    </>
  );

  return (
    <>
      {nearbyCondos.length > 0 && (
        <>
          <SectionHeader
            title={`Nearby condos in ${neighborhood.name}`}
            altText={`Nearby condos in ${neighborhood.name}`}
            containerClassName="container pt-[44px]"
            titleClassName="text-24-32-0.3 uppercase whitespace-pre-line w-max lg:text-30-36-1 md:mx-4 font-sohneBreitBuch"
          />
          {/* TODO: I don't know why this is here when it's never rendered */}
          {false && (
            <>
              <div
                className={classnames(
                  'relative h-20 pt-2 ellipsis md:leading-6 md:pr-1 text-16-26-0.3 xl:w-1/2 max-h-20 lg:max-h-40 lg:h-0',
                  {
                    'lg:h-40': neighborhood?.shortDescription,
                  }
                )}
              >
                <div
                  className={classnames('max-h-20 lg:max-h-40 h-0', {
                    'h-30 md:h-full': neighborhood?.shortDescription,
                  })}
                >
                  {neighborhood?.shortDescription && (
                    <MarkdownParser markdown={neighborhood.shortDescription} />
                  )}
                  <div
                    className="absolute bottom-0 w-full h-10"
                    style={{
                      background: 'linear-gradient(rgba(255,255,255,0),#fff)',
                    }}
                  />
                </div>
              </div>
              <Link
                title={`Read more about ${neighborhood.name}`}
                passHref
                href={`/${neighborhood.slug}`}
                className="flex items-center w-auto pl-2 mt-4 uppercase cursor-pointer border-gold md:mt-8 md:mb-10 lg:my-6"
              >
                <div className="flex items-center border-b-2 border-gold">
                  <div className="w-5 h-4">
                    <Image src={arrowLeft} alt="Read more" width={15} />
                  </div>
                  <span className="pt-1">
                    Read more about {neighborhood.name}
                  </span>
                </div>
              </Link>
            </>
          )}

          <section className="container pt-1 mt-6 pb-[41px] md:pb-0 md:mb-2 lg:pb-[64px]">
            {nearbyCondos.length > 0 ? (
              <ResponsiveCarousel
                className="nearby-condos-carousel"
                containerClassName="relative mb-2 md:mb-4 lg:mb-14"
                arrowHeight={{
                  mobile:
                    'absolute top-[50%] -translate-y-1/2 flex items-center px-3 md:px-3.5 xl:px-4 transition-colors',
                  sm: 'absolute top-[50%] -translate-y-1/2 flex items-center px-3 md:px-3.5 xl:px-4 transition-colors',
                  tablet:
                    'absolute top-[50%] -translate-y-1/2 flex items-center px-3 md:px-3.5 xl:px-4 transition-colors',
                  desktop:
                    'absolute top-[50%] -translate-y-1/2 flex items-center px-3 md:px-3.5 xl:px-4 transition-colors',
                  xl: 'absolute top-[50%] -translate-y-1/2 flex items-center px-3 md:px-3.5 xl:px-4 transition-colors',
                }}
              >
                {nearbyCondos?.map((item, index) => (
                  <div key={index}>
                    <CondoCard
                      isPPC={true}
                      listing={item}
                      index={index}
                      mediumHeight={true}
                      showDeltaPrice={true}
                      showWordingForDeltaPrice={true}
                      carouselResponsivness={true}
                    />
                  </div>
                ))}
              </ResponsiveCarousel>
            ) : (
              <Preloader />
            )}
            <div className="flex items-center justify-center mx-auto mt-16 md:w-1/2 lg:w-[404px] lg:pr-0 lg:pl-[38px]">
              <Button name="View All" href={searchPageLink} />
            </div>
          </section>
          <LineBetweenSections hideLine={'container'} />
        </>
      )}
    </>
  );
};

export default NearbyCondos;
